/**
 * Session = the bundle of Cognito tokens we store between page loads.
 * Both the email/password (SRP) and the social-login (OAuth code) paths
 * write into the same `localStorage` key so the rest of the app sees one shape.
 */
export type Session = {
  idToken: string;
  accessToken: string;
  refreshToken: string;
  expiresAt: number; // epoch seconds
  email: string;
  sub: string;
};

const KEY = "tlp.session";

export const env = {
  region: import.meta.env.VITE_COGNITO_REGION || "us-east-1",
  userPoolId: import.meta.env.VITE_COGNITO_USER_POOL_ID,
  clientId: import.meta.env.VITE_COGNITO_CLIENT_ID,
  domain: import.meta.env.VITE_COGNITO_DOMAIN,        // https://auth.thelivephotos.com
  redirectUri: import.meta.env.VITE_REDIRECT_URI,     // https://app.thelivephotos.com/auth/callback
};

export function load(): Session | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    return JSON.parse(raw) as Session;
  } catch {
    return null;
  }
}

export function save(s: Session) {
  localStorage.setItem(KEY, JSON.stringify(s));
}

export function clear() {
  localStorage.removeItem(KEY);
}

export function decodeJwt(token: string): any {
  const [, payload] = token.split(".");
  return JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));
}

export function buildSession(idToken: string, accessToken: string, refreshToken: string): Session {
  const claims = decodeJwt(idToken);
  return {
    idToken,
    accessToken,
    refreshToken,
    expiresAt: claims.exp,
    email: claims.email || "",
    sub: claims.sub,
  };
}

/** Ask Cognito for fresh tokens using the stored refresh_token. Returns null on failure. */
export async function refresh(): Promise<Session | null> {
  const cur = load();
  if (!cur?.refreshToken) return null;

  const body = new URLSearchParams({
    grant_type: "refresh_token",
    client_id: env.clientId,
    refresh_token: cur.refreshToken,
  });

  const r = await fetch(`${env.domain}/oauth2/token`, {
    method: "POST",
    headers: { "content-type": "application/x-www-form-urlencoded" },
    body,
  });
  if (!r.ok) {
    clear();
    return null;
  }
  const j = await r.json();
  // refresh response doesn't return a new refresh_token — keep the existing one.
  const next = buildSession(j.id_token, j.access_token, cur.refreshToken);
  save(next);
  return next;
}

/** Returns a non-expired idToken, refreshing if it's within `skewSeconds` of expiry. */
export async function getValidIdToken(skewSeconds = 60): Promise<string | null> {
  const cur = load();
  if (!cur) return null;
  const now = Math.floor(Date.now() / 1000);
  if (cur.expiresAt > now + skewSeconds) return cur.idToken;
  const refreshed = await refresh();
  return refreshed?.idToken ?? null;
}
